import React, { useMemo, useState } from "react";
import "./MultiStepForm.css";

const initialData = {
  name: "",
  email: "",
  goal: "",
  location: "",
  priceRange: "",
  timeline: "",
  notes: "",
  fax: "", // honeypot
  consent: false,
};

const prettyGoal = (g) => {
  switch (g) {
    case "buying":
      return "Buying a home";
    case "selling":
      return "Selling a home";
    case "both":
      return "Buying & Selling";
    case "investing":
      return "Investing";
    default:
      return g || "—";
  }
};

const prettyPrice = (p) => {
  switch (p) {
    case "under-250k":
      return "Under $250,000";
    case "250k-400k":
      return "$250,000 – $400,000";
    case "400k-600k":
      return "$400,000 – $600,000";
    case "600k+":
      return "$600,000+";
    default:
      return p || "—";
  }
};

const prettyTimeline = (t) => {
  switch (t) {
    case "asap":
      return "ASAP";
    case "1-3-months":
      return "1–3 months";
    case "3-6-months":
      return "3–6 months";
    case "just-browsing":
      return "Just browsing";
    default:
      return t || "—";
  }
};

export default function MultiStepForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialData);
  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | loading | success | error

  const submitted = status.state === "success";

  // Validate only when submitting (not for "Next")
  const submitIsValid = useMemo(() => {
    return !!formData.name.trim() && !!formData.email.trim() && !!formData.goal && !!formData.consent;
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((d) => ({ ...d, [name]: type === "checkbox" ? checked : value }));
  };

  const handleNext = (e) => {
    e?.preventDefault(); // extra-safe: never let the form do anything on Next
    setStatus({ state: "idle", msg: "" });
    setStep((s) => (s < 3 ? s + 1 : s));
  };

  const handleBack = (e) => {
    e?.preventDefault();
    setStatus({ state: "idle", msg: "" });
    setStep((s) => (s > 1 ? s - 1 : s));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // honeypot check
    if (formData.fax) return;

    // Validate on submit only
    if (!submitIsValid) {
      setStatus({
        state: "error",
        msg: "Please fill out Name, Email, your goal, and check the consent box before submitting.",
      });
      setStep(1);
      return;
    }

    setStatus({ state: "loading", msg: "" });

    try {
      const payload = {
        formType: "inquiry",
        name: formData.name.trim(),
        email: formData.email.trim(),
        goal: formData.goal,
        goalLabel: prettyGoal(formData.goal),

        location: formData.location.trim(),
        priceRange: formData.priceRange,
        priceRangeLabel: prettyPrice(formData.priceRange),

        timeline: formData.timeline,
        timelineLabel: prettyTimeline(formData.timeline),

        notes: formData.notes.trim(),
        consent: formData.consent,
        fax: formData.fax,
      };

      const r = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${r.status})`);
      }

      setStatus({
        state: "success",
        msg: "Thanks! Your inquiry was sent — Brooke will follow up soon.",
      });
      setFormData(initialData);
      setStep(1);
    } catch (err) {
      console.error(err);
      setStatus({
        state: "error",
        msg: "Something went wrong sending your inquiry. Please try again.",
      });
    }
  };

  if (submitted) {
    return (
      <div className="multistep-wrapper">
        <p className="multistep-success">{status.msg}</p>
      </div>
    );
  }

  return (
    <div className="multistep-wrapper">
      <div className="multistep-progress">
        <div className={`multistep-step ${step >= 1 ? "active" : ""}`}>1</div>
        <div className={`multistep-step ${step >= 2 ? "active" : ""}`}>2</div>
        <div className={`multistep-step ${step >= 3 ? "active" : ""}`}>3</div>
      </div>

      {/* noValidate prevents the browser's required validation UI from interfering with navigation */}
      <form className="multistep-form" onSubmit={handleSubmit} noValidate>
        {/* Honeypot */}
        <input
          type="text"
          name="fax"
          value={formData.fax}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        />

        {/* STEP 1 — CONTACT + GOAL */}
        {step === 1 && (
          <div className="multistep-panel">
            <h3>Step 1: About You</h3>

            <div className="multistep-field">
              <label>Name</label>
              {/* removed required so Next is never blocked */}
              <input type="text" name="name" value={formData.name} onChange={handleChange} />
            </div>

            <div className="multistep-field">
              <label>Email</label>
              {/* removed required so Next is never blocked */}
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>

            <div className="multistep-field">
              <label>What are you looking to do?</label>
              {/* removed required so Next is never blocked */}
              <select name="goal" value={formData.goal} onChange={handleChange}>
                <option value="">Select one</option>
                <option value="buying">Buy a home</option>
                <option value="selling">Sell a home</option>
                <option value="both">Buy & Sell</option>
                <option value="investing">Invest in property</option>
              </select>
            </div>
          </div>
        )}

        {/* STEP 2 — PROPERTY DETAILS */}
        {step === 2 && (
          <div className="multistep-panel">
            <h3>Step 2: Property Details</h3>

            <div className="multistep-field">
              <label>Preferred Location</label>
              <input
                type="text"
                name="location"
                placeholder="City, neighborhood, or ZIP"
                value={formData.location}
                onChange={handleChange}
              />
            </div>

            <div className="multistep-field">
              <label>Price Range</label>
              <select name="priceRange" value={formData.priceRange} onChange={handleChange}>
                <option value="">Select a range</option>
                <option value="under-250k">Under $250,000</option>
                <option value="250k-400k">$250,000 – $400,000</option>
                <option value="400k-600k">$400,000 – $600,000</option>
                <option value="600k+">$600,000+</option>
              </select>
            </div>

            <div className="multistep-field">
              <label>Ideal Timeline</label>
              <select name="timeline" value={formData.timeline} onChange={handleChange}>
                <option value="">Select one</option>
                <option value="asap">ASAP</option>
                <option value="1-3-months">1–3 months</option>
                <option value="3-6-months">3–6 months</option>
                <option value="just-browsing">Just browsing</option>
              </select>
            </div>
          </div>
        )}

        {/* STEP 3 — REVIEW */}
        {step === 3 && (
          <div className="multistep-panel">
            <h3>Step 3: Review & Submit</h3>

            <p className="multistep-review-intro">Here’s a summary of what you’ve shared:</p>

            <ul className="multistep-review-list">
              <li>
                <strong>Name:</strong> {formData.name || "—"}
              </li>
              <li>
                <strong>Email:</strong> {formData.email || "—"}
              </li>
              <li>
                <strong>Goal:</strong> {prettyGoal(formData.goal)}
              </li>
              <li>
                <strong>Location:</strong> {formData.location || "—"}
              </li>
              <li>
                <strong>Price Range:</strong> {prettyPrice(formData.priceRange)}
              </li>
              <li>
                <strong>Timeline:</strong> {prettyTimeline(formData.timeline)}
              </li>
            </ul>

            <div className="multistep-field">
              <label>Anything else you'd like me to know?</label>
              <textarea
                name="notes"
                rows="3"
                placeholder="Optional"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>
            <div className="consent-row">
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  style={{ marginTop: 4 }}
                  required
                />
                <span style={{ fontSize: "0.9rem", lineHeight: 1.45, color: "var(--text-secondary)" }}>
                  I agree to be contacted by Brooke via email (and phone if provided) about my inquiry.
                  I understand I can unsubscribe at any time.
                </span>
              </label>
            </div>

          </div>
        )}

        {/* STATUS */}
        {status.state === "error" && (
          <div style={{ marginTop: 10, color: "#b00020", fontWeight: 600, textAlign: "center" }}>
            {status.msg}
          </div>
        )}

        {/* ACTIONS */}
        <div className="multistep-actions">
          {step > 1 && (
            <button type="button" className="btn secondary small multistep-submit" onClick={handleBack}>
              ← Back
            </button>
          )}

          {step < 3 && (
            <button type="button" className="btn primary small multistep-submit" onClick={handleNext}>
              Next →
            </button>
          )}

          {step === 3 && (
            <button
              type="submit"
              className="btn primary small multistep-submit"
              disabled={status.state === "loading" || !formData.consent}
            >
              {status.state === "loading" ? "Submitting..." : "Submit Inquiry"}
            </button>


          )}
        </div>
      </form>
    </div>
  );
}
